import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Info } from 'lucide-react';
import { useStores } from '../hooks/useStores';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductForm = ({ product, onClose }) => {
  const { storeOptions, stores, resolveStoreId } = useStores();
  const [name, setName] = useState('');
  // `store` guarda el store_id canónico
  const [store, setStore] = useState(stores && stores.length > 0 ? stores[0].id : '');
  const [costPrice, setCostPrice] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [sku, setSku] = useState('');
  const [brand, setBrand] = useState('');
  const [barcode, setBarcode] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [category, setCategory] = useState('');
  const [stock, setStock] = useState('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [weight, setWeight] = useState('');
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (product) {
      setName(product.name);
      setStore(resolveStoreId(product.store_id || product.store) || '');
      setCostPrice(product.cost_price ? product.cost_price.toString() : '');
      setSalePrice(product.sale_price ? product.sale_price.toString() : '');
      setSku(product.sku || '');
      setBrand(product.brand || '');
      setBarcode(product.barcode || '');
      setExpiryDate(product.expiry_date || '');
      setCategory(product.category || '');
      setStock(product.stock !== undefined ? product.stock.toString() : '');
      setSummary(product.summary || '');
      setDescription(product.description || '');
      setImageUrl(product.image_url || '');
      setWeight(product.weight ? product.weight.toString() : '');
    } else if (stores && stores.length > 0) {
      // Para nuevo producto, usar la primera tienda disponible
      setStore(stores[0].id);
    }
  }, [product, stores, resolveStoreId]);

  // Filtrar categorías por tienda seleccionada
  const availableCategories = React.useMemo(() => {
    if (!store || !stores || stores.length === 0) return [];
    
    // `store` es el store_id canónico
    const selectedStore = stores.find(s => s.id === store);
    const selectedStoreName = selectedStore?.name;
    const selectedStoreId = selectedStore?.id;
    
    if (!selectedStoreName && !selectedStoreId) return [];
    
    // Filtrar categorías que pertenecen a esa tienda
    // O que no tienen tienda asignada (store === null o undefined)
    return categories
      .filter(c => {
        // Incluir si no tiene tienda asignada (categorías globales)
        if (!c.store) return true;
        // O si coincide con el nombre de la tienda
        if (c.store === selectedStoreName) return true;
        // O si coincide con el id de la tienda
        if (c.store === selectedStoreId) return true;
        return false;
      })
      .map(c => c.name)
      .sort();
  }, [categories, store, stores]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        name,
        store_id: store || null,
        cost_price: costPrice ? parseInt(costPrice) : 0,  // Convertir a entero
        sale_price: salePrice ? parseInt(salePrice) : 0,  // Convertir a entero
        sku: sku || null,
        brand: brand || null,
        barcode: barcode || null,
        expiry_date: expiryDate || null,
        category: category || null,
        stock: stock ? parseInt(stock) : 0,
        summary: summary || null,
        description: description || null,
        image_url: imageUrl || null,
        weight: weight ? parseFloat(weight) : null
      };

      if (product) {
        await axios.put(`${API}/products/${product.id}`, data);
        toast.success('Producto actualizado');
        
        // Sincronizar con PrestaShop si el producto está vinculado
        if (product.prestashop_id && product.prestashop_integration_id) {
          try {
            // Sincronizar cambios con PrestaShop (stock, price, image)
            await axios.post(
              `${API}/integrations/prestashop/${product.prestashop_integration_id}/sync-product-to-ps`,
              {
                product_id: product.id,
                sync_fields: ['stock', 'price', 'name', 'barcode', 'weight']  // SKU es readonly desde PrestaShop
              }
            );
            toast.success('✓ Sincronizado con PrestaShop');
          } catch (error) {
            console.error('Error sincronizando con PrestaShop:', error);
            toast.error('Advertencia: No se pudo sincronizar con PrestaShop');
          }
        }
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Producto creado');
      }

      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
      console.error('Error saving product:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const syncStockToPrestaShop = async (integrationId, productId, quantity) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/integrations/prestashop/${integrationId}/stock/sync-to-ps`,
        null,
        {
          params: {
            product_id: productId,
            quantity: quantity,
            source: 'manual'
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      // No mostrar toast para no ser intrusivo, pero loggear éxito
      console.log('✅ Stock sincronizado con PrestaShop');
    } catch (error) {
      // Solo loggear error, no mostrar toast para no interrumpir el flujo
      console.error('⚠️ Error sincronizando stock con PrestaShop:', error);
    }
  };

  // Cálculos automáticos
  const ivaAmount = salePrice ? parseFloat(salePrice) / 1.19 * 0.19 : 0;
  const margin = costPrice && salePrice 
    ? parseFloat(salePrice) - parseFloat(costPrice) - ivaAmount 
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div 
        className="bg-white border-2 border-slate-900 rounded-xl p-6 md:p-8 w-full max-w-2xl"
        style={{ boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)' }}
        data-testid="product-form-modal"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 
            className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            {product ? 'Editar Producto' : 'Nuevo Producto'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="close-product-form"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
              Nombre del Producto *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 focus:ring-0 focus:outline-none focus:border-indigo-500 transition-all"
              required
              data-testid="product-name-input"
            />
          </div>

          {/* Store */}
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
              Tienda *
            </label>
            <select
              value={store}
              onChange={(e) => setStore(e.target.value)}
              className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 cursor-pointer focus:ring-0 focus:outline-none focus:border-indigo-500"
              data-testid="product-store-select"
            >
              {storeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* SKU, Brand and Category Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                SKU (Código) - Desde PrestaShop
              </label>
              <input
                type="text"
                value={sku}
                readOnly
                disabled
                className="w-full bg-slate-100 border-2 border-slate-300 rounded-xl px-4 py-3 font-medium text-slate-600 cursor-not-allowed"
                placeholder="SKU desde PrestaShop"
                data-testid="product-sku-input"
              />
              <p className="text-xs mt-1 text-slate-500">
                ℹ️ El SKU es de solo lectura desde PrestaShop
              </p>
            </div>
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                Marca
              </label>
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 focus:ring-0 focus:outline-none focus:border-indigo-500"
                placeholder="Ej: Nike, Apple, etc."
                data-testid="product-brand-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                Categoría
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 cursor-pointer focus:ring-0 focus:outline-none focus:border-indigo-500"
                data-testid="product-category-select"
              >
                <option value="">Sin categoría</option>
                {availableCategories.map((cat, idx) => (
                  <option key={idx} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Barcode, Expiry Date, and Stock Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                Código de Barra
              </label>
              <input
                type="text"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium font-mono text-slate-900 focus:ring-0 focus:outline-none focus:border-indigo-500"
                placeholder="Ej: 7501234567890"
                data-testid="product-barcode-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                Fecha de Vencimiento
              </label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 focus:ring-0 focus:outline-none focus:border-indigo-500 cursor-pointer"
                data-testid="product-expiry-date-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                Stock Disponible
              </label>
              <input
                type="number"
                step="1"
                min="0"
                value={stock}
                onChange={(e) => setStock(e.target.value)}
                className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 focus:ring-0 focus:outline-none focus:border-indigo-500"
                placeholder="0"
                data-testid="product-stock-input"
              />
            </div>
          </div>

          {/* Resumen (descripción corta) */}
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
              Resumen (Descripción Corta)
            </label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows="2"
              className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 focus:ring-0 focus:outline-none focus:border-indigo-500 resize-y"
              placeholder="Descripción breve del producto"
              data-testid="product-summary-input"
            />
          </div>

          {/* Descripción completa */}
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
              Descripción Completa
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="4"
              className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 focus:ring-0 focus:outline-none focus:border-indigo-500 resize-y font-mono text-xs"
              placeholder="Descripción detallada (puede contener HTML si viene de PrestaShop)"
              data-testid="product-description-input"
            />
          </div>

          {/* URL de Imagen y Peso */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                URL de Imagen
              </label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 focus:ring-0 focus:outline-none focus:border-indigo-500"
                placeholder="https://ejemplo.com/imagen.jpg"
                data-testid="product-image-url-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                URL de la imagen de portada del producto
              </p>
            </div>
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                Peso (kg)
              </label>
              <input
                type="number"
                step="0.001"
                min="0"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 focus:ring-0 focus:outline-none focus:border-indigo-500"
                placeholder="0.000"
                data-testid="product-weight-input"
              />
            </div>
          </div>

          {/* Prices Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                Precio Costo
                <div className="relative group">
                  <Info className="w-4 h-4 text-slate-400 cursor-help" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block w-64 z-50">
                    <div className="bg-slate-900 text-white text-xs rounded-lg p-3 shadow-xl border-2 border-slate-700">
                      <p className="font-normal leading-relaxed">
                        Si no ingresas el precio de costo, no podremos calcular cuánto ganas en cada venta. 
                        Es importante para mejorar tu negocio.
                      </p>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                        <div className="border-8 border-transparent border-t-slate-900"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </label>
              <input
                type="number"
                step="1"
                value={costPrice}
                onChange={(e) => setCostPrice(e.target.value)}
                className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 focus:ring-0 focus:outline-none focus:border-indigo-500 transition-all"
                placeholder="Opcional"
                data-testid="product-cost-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                Precio Venta *
              </label>
              <input
                type="number"
                step="1"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium text-slate-900 focus:ring-0 focus:outline-none focus:border-indigo-500 transition-all"
                required
                placeholder="0"
                data-testid="product-sale-price-input"
              />
            </div>
          </div>

          {/* Calculations Preview */}
          {costPrice && salePrice && (
            <div 
              className="bg-slate-100 border-2 border-slate-900 rounded-xl p-4"
              data-testid="product-calculations"
            >
              <p className="text-xs font-bold tracking-widest uppercase text-slate-500 mb-3">Desglose Automático</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-slate-600">Precio Costo:</span>
                  <span className="font-mono font-bold">${parseInt(costPrice || 0).toLocaleString('es-CL')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-slate-600">IVA (19%):</span>
                  <span className="font-mono font-bold text-blue-600">${Math.round(ivaAmount).toLocaleString('es-CL')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-slate-600">Margen Utilidad:</span>
                  <span className="font-mono font-bold text-green-600">${Math.round(margin).toLocaleString('es-CL')}</span>
                </div>
                <div className="flex justify-between pt-2 border-t-2 border-slate-900">
                  <span className="text-sm font-bold text-slate-900">Precio Venta:</span>
                  <span className="font-mono font-bold text-lg text-slate-900">${parseInt(salePrice || 0).toLocaleString('es-CL')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-white text-slate-900 border-2 border-slate-900 rounded-xl px-6 py-3 font-bold transition-all"
              style={{ boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)' }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 text-slate-900 border-2 border-slate-900 rounded-xl px-6 py-3 font-bold transition-all"
              style={{
                backgroundColor: '#D4F0A5',
                boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              data-testid="product-submit-btn"
            >
              {loading ? 'Guardando...' : (product ? 'Actualizar' : 'Crear Producto')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
